import React, { useMemo, useEffect } from "react";
import { useParams, useMatch } from "react-router-dom";
import { is, isEmpty, isNil, path } from "ramda";
import { Page } from "../../layout/Page";
import { Section } from "../../layout/Section";
import { useT } from "../../hooks/useT";
import { project } from "../../queries";
import { useQ } from "../../hooks/useQ";
import { CheckpointReport } from "./components/CheckpointReport";
import { HighlightReport } from "./components/HighlightReport";
import { clientUrl } from "../../util/routing";
import { uris } from "../../config/nav";
import { HighlightReportPdf } from "./HighlightReportPdf";
import { Container } from "@mui/material";
import { useFlag } from "@/hooks/useFlag";

export const Progress = () => {
  const { id = "", reportId = "", type = "highlight" } = useParams();
  const { t } = useT();
  const useNewCheckpoint = useFlag("checkpoints.useRestApiV1");
  const { isLoading: isLoadingProject, data = {} } = useQ(`project-${id}`, () =>
    project.single({ id }),
  );

  const {
    isLoading: isLoadingReportList,
    data: reportObject = {},
    refetch: refetchReportList,
  } = useQ(`project-${id}-report-${reportId}`, () =>
    project.report.single({ id, type, reportId }),
  );
  const { data: projectLessonsData = {}, refetch: refetchProjectLessons } =
    useQ(`project-lessons`, () => project.lessons());

  const lessons = projectLessonsData.lessons ?? [];

  useEffect(() => {
    refetchReportList();
  }, [id, reportId, type, refetchReportList]);

  const { isLoading: isLoadingPlans, data: projectPlanData = [] } = useQ(
    `project-${id}-plans`,
    () => project.plans({ id }),
  );

  const report = useMemo(() => {
    return reportObject && reportObject.id
      ? {
          ...reportObject,
          summary: path(["summary", "content"], reportObject),
          value: path(["summary", "status"], reportObject),
        }
      : null;
  }, [reportObject, reportId, type, useNewCheckpoint]);

  const { title } = data;

  const isFullscreen = useMatch(
    clientUrl(uris.project.progressFullscreen, {
      id,
      type: "highlight",
      reportId,
    }),
  );

  const isLoading = isLoadingProject || isLoadingPlans || isLoadingReportList;

  useEffect(() => {
    if (isFullscreen) {
      document.body.style.background = "none";
    }
  }, [isFullscreen]);

  const renderContent = () => {
    return (
      <>
        {useMatch(
          clientUrl(uris.project.progressFull, {
            id,
            type: "checkpoint",
            reportId,
          }),
        ) &&
          (useNewCheckpoint ? (
            <HighlightReport
              initiative={data}
              report={report}
              type="checkpoint"
            />
          ) : (
            <CheckpointReport projectId={id} reportId={reportId} />
          ))}

        {useMatch(
          clientUrl(uris.project.progressFull, {
            id,
            type: "highlight",
            reportId,
          }),
        ) && <HighlightReport initiative={data} report={report} />}
        {useMatch(
          clientUrl(uris.project.progressFullscreen, {
            id,
            type: "highlight",
            reportId,
          }),
        ) && <HighlightReport initiative={data} report={report} />}
        {useMatch(
          clientUrl(uris.project.progressFull, {
            id,
            type: "endStage",
            reportId,
          }),
        ) && (
          <HighlightReport
            initiative={data}
            report={report}
            type={type}
            stages={projectPlanData.stages ?? []}
            lessons={lessons}
          />
        )}
        {useMatch(
          clientUrl(uris.project.progressFull, {
            id,
            type: "endProject",
            reportId,
          }),
        ) && (
          <HighlightReport
            initiative={data}
            report={report}
            type={type}
            lessons={lessons}
          />
        )}
        {useMatch(
          clientUrl(uris.project.progressReportIdPdf, {
            id,
            type,
            reportId,
          }),
        ) && (
          <HighlightReportPdf
            projectId={id}
            reportId={reportId}
            type={type}
            stages={projectPlanData.stages ?? []}
          />
        )}
      </>
    );
  };

  if (isFullscreen) {
    return <Container sx={{ py: 4 }}>{renderContent()}</Container>;
  } else {
    return (
      <Page isLoading={isLoading} title={title}>
        <Section title={t("project.progress.title")}>{renderContent()}</Section>
      </Page>
    );
  }
};
